import React, { useState } from 'react';
import { motion, Reorder } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { GripVerticalIcon, PlusIcon, XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { twMerge } from 'tailwind-merge';
import { WizardLayout } from '../components/WizardLayout';
import { KeysEditor } from '../components/performer/KeysEditor';
import { PerformerPreferencesForm } from '../components/performer/PerformerPreferencesForm';
import { SegmentedControl } from '../components/ui/SegmentedControl';
import { NumberStepper } from '../components/ui/NumberStepper';
import { RangeSlider } from '../components/ui/RangeSlider';
import { useMarketplace } from '../contexts/MarketplaceContext';
import { performerPhotoLibrary } from '../data/images';
import { baht } from '../utils/format';
import { spring } from '../utils/motion';
import type { ActType, PerformerPrefs, SongKey } from '../types/marketplace';
const steps = [{
  title: 'Tell us about your act',
  subtitle: 'This is the first thing organisers see.'
}, {
  title: 'Add your best photos',
  subtitle: 'Drag to reorder — the first one is your cover.'
}, {
  title: 'Your Keys',
  subtitle: 'Songs you can perform. Organisers search and filter by these.'
}, {
  title: 'Price & travel',
  subtitle: 'Organisers see your fee plus an automatic travel estimate.'
}, {
  title: 'What gigs do you want?',
  subtitle: 'We use this to rank events and explain every suggestion.'
}];
const field = 'h-14 w-full rounded-sm border border-line bg-surface px-4 text-[17px] font-semibold placeholder:font-normal placeholder:text-ink-3 focus:border-ink focus:outline-none';
export function PerformerOnboarding() {
  const navigate = useNavigate();
  const {
    me,
    updateMe,
    performerPrefs,
    setPerformerPrefs,
    setRole
  } = useMarketplace();
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [actType, setActType] = useState<ActType>('Solo singer');
  const [members, setMembers] = useState(1);
  const [name, setName] = useState('');
  const [photos, setPhotos] = useState<string[]>([]);
  const [keys, setKeys] = useState<SongKey[]>(me.keys.slice(0, 2));
  const [price, setPrice] = useState(8000);
  const [home, setHome] = useState('Ari, Bangkok');
  const [radius, setRadius] = useState(150);
  const [travelMode, setTravelMode] = useState<'km' | 'flat'>('km');
  const [perKm, setPerKm] = useState(8);
  const [flat, setFlat] = useState(800);
  const [prefs, setPrefs] = useState<PerformerPrefs>(performerPrefs);
  const go = (d: number) => {
    setDir(d);
    setStep(s => s + d);
  };
  const finish = () => {
    updateMe({
      name: name.trim() || me.name,
      actType,
      members: actType === 'Solo singer' ? 1 : members,
      photos: photos.length ? photos : me.photos,
      keys,
      price,
      city: home,
      travelRadiusKm: radius,
      travelCostPerKm: travelMode === 'km' ? perKm : Math.max(1, Math.round(flat / 20))
    });
    setPerformerPrefs(prefs);
    setRole('performer');
    toast.success('Your profile is live', {
      description: 'Here are the gigs that fit you best.'
    });
    navigate('/p/discover');
  };
  const canNext = [name.trim().length > 1, true, keys.length > 0, price > 0, prefs.genres.length > 0][step];
  return <WizardLayout step={step} total={steps.length} direction={dir} title={steps[step].title} subtitle={steps[step].subtitle} onBack={() => step === 0 ? navigate('/start') : go(-1)} onNext={() => step === steps.length - 1 ? finish() : go(1)} nextLabel={step === steps.length - 1 ? 'Show me gigs' : 'Continue'} canNext={canNext} onSkip={step === 1 || step === 2 ? () => go(1) : undefined} data-id="element-1309" data-mp="zw2sff3cojuz">
      {step === 0 && <div className="space-y-7" data-id="element-1310" data-mp="1jhz2nbz4yfhf">
          <SegmentedControl<ActType> ariaLabel="Act type" layoutId="ob-act" className="flex w-full" value={actType} onChange={t => {
        setActType(t);
        setMembers(t === 'Duo' ? 2 : t === 'Band' ? 4 : 1);
      }} options={[{
        value: 'Solo singer',
        label: 'Solo singer'
      }, {
        value: 'Duo',
        label: 'Duo'
      }, {
        value: 'Band',
        label: 'Band'
      }]} data-id="element-1311" data-mp="3pc09doregu0y" />
          <label className="block" data-id="element-1312" data-mp="4lo213d63mu1">
            <span className="text-[15px] font-bold" data-id="element-1313" data-mp="4kmqyyw5j0cb">{actType === 'Solo singer' ? 'Stage name' : 'Act name'}</span>
            <input autoFocus value={name} onChange={e => setName(e.target.value)} placeholder={actType === 'Solo singer' ? 'e.g. Ploy Siriwan' : 'e.g. The Velvet Brass'} className={`${field} mt-2`} data-id="element-1314" data-mp="383fqw8w0bqo0" />
          </label>
          {actType === 'Band' && <NumberStepper label="Members" value={members} step={1} min={3} max={14} onChange={setMembers} data-id="element-1315" data-mp="2keer1a18pnuc" />}
        </div>}

      {step === 1 && <div data-id="element-1316" data-mp="2pavd7yovps4s">
          {photos.length > 0 && <Reorder.Group axis="x" values={photos} onReorder={setPhotos} className="no-scrollbar mb-6 flex gap-3 overflow-x-auto pb-1" data-id="element-1317" data-mp="2htp3lh7bv0xn">
              {photos.map((p, i) => <Reorder.Item key={p} value={p} className="relative h-36 w-28 shrink-0 cursor-grab touch-none active:cursor-grabbing" whileDrag={{
          scale: 1.05
        }} data-id="element-1318" data-mp="27uzjdhryl4e8" data-mpi={i}>
                  <img src={p} alt="" draggable={false} className="h-full w-full rounded-md object-cover" data-id="element-1319" data-mp="23tedwhhr7se2" />
                  <span className="absolute left-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-xs font-bold text-white" data-id="element-1320" data-mp="1ywnxtu5y8wsn">{i === 0 ? 'Cover' : i + 1}</span>
                  <button type="button" onClick={() => setPhotos(l => l.filter(x => x !== p))} aria-label="Remove photo" className="absolute right-1.5 top-1.5 grid h-7 w-7 place-items-center rounded-full bg-black/60 text-white" data-id="element-1321" data-mp="1p5t82fthvf2g">
                    <XIcon className="h-4 w-4" data-id="element-1322" data-mp="1b5s60x1hu1le" />
                  </button>
                  <GripVerticalIcon className="absolute bottom-2 right-2 h-4 w-4 text-white" aria-hidden="true" data-id="element-1323" data-mp="11glcccpypnis" />
                </Reorder.Item>)}
            </Reorder.Group>}
          <p className="mb-3 text-[15px] font-bold" data-id="element-1324" data-mp="rki913jdjiu9">Choose from your library</p>
          <div className="grid grid-cols-3 gap-3" data-id="element-1325" data-mp="whi6iq25rd9r">
            {performerPhotoLibrary.map(p => {
          const on = photos.includes(p);
          return <motion.button key={p} type="button" whileTap={{
            scale: 0.95
          }} transition={spring.snappy} onClick={() => setPhotos(l => on ? l.filter(x => x !== p) : [...l, p])} aria-pressed={on} aria-label={on ? 'Remove photo' : 'Add photo'} className={twMerge('relative aspect-square overflow-hidden rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand', on && 'opacity-40')} data-id="element-1326" data-mp="1p6z2tyswfeb9">
                  <img src={p} alt="" className="h-full w-full object-cover" data-id="element-1327" data-mp="3slrfad8w5cbb" />
                  {!on && <span className="absolute bottom-2 right-2 grid h-8 w-8 place-items-center rounded-full bg-surface text-ink shadow" data-id="element-1328" data-mp="3nmy6gy4ju9hh">
                      <PlusIcon className="h-4 w-4" data-id="element-1329" data-mp="18y2lx2wagf0" />
                    </span>}
                </motion.button>;
        })}
          </div>
        </div>}

      {step === 2 && <KeysEditor keys={keys} onChange={setKeys} data-id="element-1330" data-mp="34qybdeg9ypto" />}

      {step === 3 && <div className="space-y-7" data-id="element-1331" data-mp="3iq7k5t25ihnm">
          <NumberStepper label="Your fee" hint="Per performance" value={price} step={500} min={1000} onChange={setPrice} format={baht} data-id="element-1332" data-mp="3ir94ko524gi0" />
          <label className="block" data-id="element-1333" data-mp="xzgt388p43bm">
            <span className="text-[15px] font-bold" data-id="element-1334" data-mp="xyfggzc178rs">Home base</span>
            <input value={home} onChange={e => setHome(e.target.value)} className={`${field} mt-2`} data-id="element-1335" data-mp="t0mc8buagou5" />
          </label>
          <RangeSlider label="How far will you travel?" min={5} max={1000} step={5} value={radius} onChange={setRadius} format={v => `${v} km`} data-id="element-1336" data-mp="1d39tugthkhfd" />
          <div data-id="element-1337" data-mp="1hzh0ycxmbyyw">
            <p className="mb-3 text-[15px] font-bold" data-id="element-1338" data-mp="1hz7daw01q3wd">Travel costs</p>
            <SegmentedControl<'km' | 'flat'> ariaLabel="Travel cost type" layoutId="ob-travel" className="flex w-full max-w-xs" size="sm" value={travelMode} onChange={setTravelMode} options={[{
          value: 'km',
          label: 'Per km'
        }, {
          value: 'flat',
          label: 'Flat fee'
        }]} data-id="element-1339" data-mp="1m1vk6y5790qp" />
            <div className="mt-4" data-id="element-1340" data-mp="25pbeyxtobtkq">
              {travelMode === 'km' ? <NumberStepper label="Cost per km" hint="Charged on the round trip" value={perKm} step={1} min={0} max={50} onChange={setPerKm} format={v => `฿${v}`} data-id="element-1341" data-mp="29ry8d35ztmk1" /> : <NumberStepper label="Flat travel fee" hint="Added to every booking" value={flat} step={100} min={0} onChange={setFlat} format={baht} data-id="element-1342" data-mp="ff8cwgzz85aj" />}
            </div>
          </div>
        </div>}

      {step === 4 && <PerformerPreferencesForm value={prefs} onChange={setPrefs} data-id="element-1343" data-mp="3uarzbjnfckm1" />}
    </WizardLayout>;
}